import { useEffect, useRef, useState } from 'react'
import {
  createChart,
  CandlestickSeries,
  ColorType,
  type IChartApi,
  type ISeriesApi,
} from 'lightweight-charts'
import { useTradingData } from '@/shared/hooks'
import { fmtUsd, fmtUsdCompact } from '@/shared/lib'
import btcLogo from '@/assets/bitcoin-btc-logo.svg'
import ethLogo from '@/assets/ethereum-eth-logo.svg'
import solLogo from '@/assets/solana-sol-logo.svg'
import bnbLogo from '@/assets/bnb-bnb-logo.svg'
import xrpLogo from '@/assets/xrp-logo.svg'

const CRYPTO_TABS = [
  { symbol: 'BTC', logo: btcLogo },
  { symbol: 'ETH', logo: ethLogo },
  { symbol: 'SOL', logo: solLogo },
  { symbol: 'BNB', logo: bnbLogo },
  { symbol: 'XRP', logo: xrpLogo },
] as const

export function ChartBlock() {
  const [activeSymbol, setActiveSymbol] = useState<string>('BTC')
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const initializedRef = useRef(false)

  const {
    pairLabel, price, openPrice,
    volume24h, candles, onCandleUpdate,
  } = useTradingData(activeSymbol)

  const change = openPrice ? ((price - openPrice) / openPrice) * 100 : 0
  const isPositive = change >= 0

  useEffect(() => {
    if (!chartContainerRef.current) return

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#6b7194',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        fontSize: 12,
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.03)' },
        horzLines: { color: 'rgba(255,255,255,0.03)' },
      },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
      rightPriceScale: { borderColor: 'rgba(255,255,255,0.06)' },
      timeScale: {
        borderColor: 'rgba(255,255,255,0.06)',
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 2,
      },
      handleScroll: false,
      handleScale: false,
    })

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#10b981',
      downColor: '#ef4444',
      borderUpColor: '#10b981',
      borderDownColor: '#ef4444',
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    })

    chartRef.current = chart
    seriesRef.current = series

    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        chart.applyOptions({ width, height })
      }
    })
    ro.observe(chartContainerRef.current)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      initializedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!seriesRef.current || candles.length === 0 || initializedRef.current) return
    initializedRef.current = true
    seriesRef.current.setData(candles)
    chartRef.current?.timeScale().fitContent()
  }, [candles])

  // Reset chart data when symbol changes
  useEffect(() => {
    initializedRef.current = false
    if (seriesRef.current) {
      seriesRef.current.setData([])
    }
  }, [activeSymbol])

  useEffect(() => {
    onCandleUpdate((candle) => {
      seriesRef.current?.update(candle)
    })
  }, [onCandleUpdate])

  return (
    <div className="card p-3 sm:p-5 flex flex-col h-full min-h-[280px] sm:min-h-[320px] overflow-hidden">
      {/* Symbol tabs */}
      <div className="flex items-center gap-1 sm:gap-1.5 mb-3 shrink-0 overflow-x-auto scrollbar-none p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
        {CRYPTO_TABS.map(({ symbol, logo }) => (
          <button
            key={symbol}
            onClick={() => setActiveSymbol(symbol)}
            className={`relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-xs font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
              activeSymbol === symbol
                ? 'bg-gradient-to-b from-brand-400/15 to-brand-500/5 text-brand-400 shadow-[0_0_12px_rgba(251,191,36,0.1)] border border-brand-400/25'
                : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.04] border border-transparent'
            }`}
          >
            <img
              src={logo}
              alt={symbol}
              className={`w-4 h-4 sm:w-[18px] sm:h-[18px] transition-opacity duration-200 ${
                activeSymbol === symbol ? 'opacity-100' : 'opacity-50'
              }`}
            />
            {symbol}
            {activeSymbol === symbol && (
              <span className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-4 h-[2px] rounded-full bg-brand-400/60" />
            )}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src={CRYPTO_TABS.find(t => t.symbol === activeSymbol)?.logo} alt={activeSymbol} className="w-5 h-5 sm:w-6 sm:h-6" />
          <h2 className="text-sm sm:text-base font-extrabold text-white whitespace-nowrap">{pairLabel}</h2>
          <span className="badge-verified text-[10px] sm:text-xs">Live</span>
          <p className="text-base sm:text-xl font-extrabold text-white">{fmtUsd(price)}</p>
        </div>
        <span className={`text-[10px] sm:text-xs font-bold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg ${isPositive ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' : 'text-red-400 bg-red-500/10 border border-red-500/20'}`}>
          {isPositive ? '▲ +' : '▼ '}{change.toFixed(2)}%
        </span>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0 rounded-lg overflow-hidden" ref={chartContainerRef} />

      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-5 mt-2 pt-2 border-t border-white/[0.06] shrink-0">
        <span className="text-[10px] sm:text-xs text-gray-500">Price <span className="text-xs sm:text-sm font-bold text-white">{fmtUsd(price)}</span></span>
        <span className="text-[10px] sm:text-xs text-gray-500">24h <span className={`text-xs sm:text-sm font-bold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>{isPositive ? '+' : ''}{change.toFixed(2)}%</span></span>
        <span className="text-[10px] sm:text-xs text-gray-500">Vol <span className="text-xs sm:text-sm font-bold text-white">{fmtUsdCompact(volume24h)}</span></span>
      </div>
    </div>
  )
}
